import { Head } from "@inertiajs/react";
import CtaBand from "@/components/marketing/cta-band";
import PageHero from "@/components/marketing/page-hero";
import SectionHeading from "@/components/marketing/section-heading";
import SmartLink from "@/components/marketing/smart-link";
import TeamGrid from "@/components/marketing/team-grid";
import PublicLayout from "@/layouts/public-layout";
import { operatingPrinciples, siteLinks } from "@/data/marketing-site";

const aboutHighlights = [
  {
    title: "Vi gør det svære arbejde på forhånd",
    copy: "Vi tager stilling til politikker, kontroller og processer, så vores kunder ikke skal starte fra bunden, før de overhovedet kan begynde."
  },
  {
    title: "Vi bygger til drift, ikke til præsentationer",
    copy: "Vores løsninger er designet til at blive brugt i praksis, både som governancefundament i Core og som fuld compliance-motor i ServiceNow."
  },
  {
    title: "Vi tager ansvar for at få kunder i mål",
    copy: "The Obsero Way handler om at blive ved kundens side, til dokumentation, styring og drift hænger sammen og virker over tid."
  }
];

const leadershipNeeds = [
  {
    title: "Strategisk ro",
    copy: "Compliance skal være et fundament, ledelsen kan stole på, frem for en løbende usikkerhed om, hvorvidt noget mangler."
  },
  {
    title: "Dokumenteret ansvar",
    copy: "Beslutninger, reviews, kontroller og opfølgning skal kunne spores, så ledelsen kan dokumentere aktivt ansvar i praksis."
  },
  {
    title: "Compliance i drift",
    copy: "Målet er ikke isolerede dokumenter, men en organisation der kan arbejde videre med sikker hånd, også når kravene ændrer sig."
  }
];

function About() {
  const heroAside = (
    <article className="public-card public-card--glass h-100">
      <span className="public-card__eyebrow public-card__eyebrow--light">Derfor findes vi</span>
      <h2 className="public-card__title public-card__title--light">
        Compliance skal være et fundament frem for en byrde.
      </h2>
      <p className="public-card__copy public-card__copy--light mb-0">
        Vi gør det muligt for virksomheder at bygge det fundament hurtigt, sikkert og med sikker hånd.
      </p>
    </article>
  );

  return (
    <>
      <Head title="Om Obsero">
        <meta
          head-key="description"
          name="description"
          content="Derfor eksisterer Obsero: for at gøre strategisk ro, dokumenteret ansvar og compliance i drift muligt for virksomheder under NIS2 eller DORA."
        />
      </Head>

      <PageHero
        narrow
        size="compact"
        eyebrow="Om Obsero"
        title="Derfor eksisterer Obsero."
        copy="Compliance skal ikke være en byrde. Det skal være et fundament for strategisk ro, dokumenteret ledelsesansvar og en organisation der er klar til fremtiden."
        actions={[
          { href: siteLinks.contact, label: "Kontakt os", className: "btn btn-light" }
        ]}
        signals={["Strategisk ro", "Dokumenteret ansvar", "Compliance i drift."]}
        aside={heroAside}
      />

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead public-split-callout__lead--accented">
              <SectionHeading
                eyebrow="Fundamentet"
                title="Vi gør det muligt at bygge fundamentet hurtigt, sikkert og med sikker hånd."
                copy="NIS2, DORA og beslægtede krav rammer ledelse, drift, kontrolmiljø og dokumentation på samme tid. Derfor bygger vi ét sammenhængende fundament frem for isolerede delprojekter."
              />

              <div className="public-inline-links">
                <SmartLink href={siteLinks.core} className="public-inline-link">
                  Se Obsero Core
                </SmartLink>
                <SmartLink href={siteLinks.enterprise} className="public-inline-link">
                  Se Obsero Enterprise
                </SmartLink>
              </div>
            </div>

            <div className="public-stack-list">
              {aboutHighlights.map((item, index) => (
                <article key={item.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{item.title}</h3>
                    <p className="public-stack-item__copy">{item.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--white-band">
        <div className="public-container">
          <div className="public-statement-grid">
            <div className="public-statement-block">
              <h2 className="public-statement-block__text">
                The Obsero Way handler om at levere et gennemtestet fundament og tage ansvar for at det virker i praksis.
              </h2>
              <p className="public-statement-block__copy">
                Vores specialister har dyb erfaring fra finanssektoren, IT-sikkerhed og regulatorisk compliance, og vi er med hele vejen til kunderne er i mål.
              </p>
            </div>

            <div className="public-principle-list">
              {operatingPrinciples.map((principle, index) => (
                <article key={principle.title} className="public-principle-item">
                  <div className="public-principle-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-principle-item__body">
                    <h3 className="public-principle-item__title">{principle.title}</h3>
                    <p className="public-principle-item__copy">{principle.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--shell-band">
        <div className="public-container">
          <SectionHeading
            eyebrow="Ledelsens perspektiv"
            title="Det vigtigste spørgsmål er ikke, om der findes dokumenter."
            copy="Det vigtigste spørgsmål er, om ledelsen kan se, styre og dokumentere, hvordan organisationen faktisk arbejder med compliance over tid."
          />

          <div className="public-pillars">
            {leadershipNeeds.map((item, index) => {
              const icons = ["bi bi-compass", "bi bi-journal-check", "bi bi-arrow-repeat"];

              return (
                <article key={item.title} className="public-pillar">
                  <div className="public-icon-chip">
                    <i className={icons[index]} aria-hidden="true" />
                  </div>
                  <div className="public-pillar__body">
                    <h3 className="public-pillar__title">{item.title}</h3>
                    <p className="public-pillar__copy">{item.copy}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="public-section public-section--team-tail">
        <div className="public-container">
          <SectionHeading title="Teamet bag Obsero." className="public-team-intro" />
          <TeamGrid />
        </div>
      </section>

      <CtaBand
        eyebrow="Samarbejde"
        title="Vi hjælper jer gerne med at finde den rigtige vej ind i arbejdet."
        copy="Nogle organisationer skal starte med governancefundamentet i Core, mens andre har brug for hele compliance-motoren i ServiceNow. Vi guider jer til det rigtige valg."
        actions={[
          { href: siteLinks.core, label: "Se Obsero Core", className: "btn btn-light" },
          { href: siteLinks.enterprise, label: "Se Obsero Enterprise", className: "btn btn-outline-light" }
        ]}
      />
    </>
  );
}

About.layout = (page) => <PublicLayout>{page}</PublicLayout>;

export default About;
